import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import {
    BAD_CODE,
    CONNECTION_ERROR,
    CONNECTION_IMPOSSIBLE,
    LOCKEDOUT_MESSAGE,
} from "@shared/constants/error-messages.constants";
import { ACCUEIL } from "@shared/constants/route.constants";
import { AuthenticationService } from "@shared/services/authentication.service";
import { IdleUserService } from "@shared/services/idle-user.service";
import { MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { DividerModule } from "primeng/divider";
import { InputOtpModule } from "primeng/inputotp";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { ToastModule } from "primeng/toast";
import { switchMap } from "rxjs";

@Component({
  selector: "app-strong-authentication",
  templateUrl: "./strong-authentication.component.html",
  styleUrls: ["./strong-authentication.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    ToastModule,
    InputOtpModule,
    TranslateModule,
    DividerModule,
    ProgressSpinnerModule,
  ],
  providers: [MessageService],
})
export class StrongAuthenticationComponent implements OnInit {
  formGroup: FormGroup = new FormGroup({
    code: new FormControl<string | null>(null, {
      validators: [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      nonNullable: true,
    }),
  });

  email: string | undefined;
  password!: string;
  isLoading = false;

  readonly LOCKEDOUT = "LockedOut";

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private messageService: MessageService,
    private idleUserService: IdleUserService
  ) {}

  ngOnInit(): void {
    this.authenticationService.currentEmail.subscribe(email => (this.email = email));
  }

  onSubmit() {
    if (this.formGroup.valid) {
      this.authenticationService.currentPassword.subscribe(password => (this.password = password));
      this.authenticationService
        .sendLogin(this.email!, this.password, this.formGroup.value.code, true)
        .pipe(
          switchMap(() => this.authenticationService.verifyToken(this.email!, this.formGroup.value.code)),
          switchMap(() =>
            this.authenticationService.sendLogin(this.email!, this.password, this.formGroup.value.code, false)
          )
        )
        .subscribe({
          next: () => {
            this.idleUserService.setUserIdle();
            this.router.navigate([ACCUEIL]);
          },
          error: error => {
            if (error.detail === this.LOCKEDOUT) {
              this.displayError(LOCKEDOUT_MESSAGE);
            } else {
              this.displayError(BAD_CODE);
            }
          },
        });
    }
  }

  resendCode() {
    if (this.email) {
      this.authenticationService.sendToken(this.email).subscribe({
        next: () => {
          this.isLoading = true;
          setTimeout(() => {
            this.isLoading = false;
          }, 30000);
        },
        error: error => {
          this.messageService.add({
            severity: "error",
            summary: CONNECTION_ERROR,
            detail: CONNECTION_IMPOSSIBLE,
          });
        },
      });
    }
  }

  displayError(message: string) {
    this.messageService.add({
      severity: "error",
      summary: CONNECTION_ERROR,
      detail: message,
    });
  }
}
